import { makeAutoObservable } from 'mobx';
import axios from 'axios';
import RootStore from '../RootStore/RootStore';
import API_URL from '../../config/ApiUrl';
import { Notification, RegionType } from '../../config/Global';
import Message from '../../config/Message';

export default class AuthStore {
	// Variables
	user = null;
	token = null;
	app_loading = true;
	rootStore = null;
	accessMenu = null;
	permission = null;
	stateList = [];
	cityList = [];
	lookupKeyList = [];
	amenityList = [];

	constructor() {
		this.app_loading = true;
		this.rootStore = new RootStore();
		makeAutoObservable(this);
		window.addEventListener('storage', this.handleInvalidToken);
	}

	// Initiate Application Functions
	InitializeApp = () => {
		// this.rootStore.setNotificationConfig();
		this.rootStore.setAxiosBaseUrl();
		this.rootStore.setAxiosInterceptor();
		this.setupHeaders(localStorage.getItem('token'));
	};

	handleInvalidToken = (e) => {
		let need_redirect = false;

		if (e.key === 'token') {
			if ((e.oldValue && !e.newValue) || e.oldValue !== e.newValue) {
				need_redirect = true;
			} else if (!e.oldValue && e.newValue) {
				need_redirect = true;
			}
		}
		if (need_redirect) {
			this.customRedirect();
		}
	};

	isUnauthorized = () => {
		return this.rootStore.isUnauthorise;
	};

	getPermission = () => {
		if (this.accessMenu) {
			this.permission = this.accessMenu
				.filter((item) => item.path === window.location.pathname)
				.map((item) => item.action);
		}
	};

	// Check User Privileges
	checkPermission = (permission_type) => {
		if (
			this.permission[0] &&
			this.permission[0].includes(permission_type)
		) {
			return true;
		} else {
			return false;
		}
	};

	customRedirect(redirect = '', timeout = 1000) {
		if (this.rootStore.login_redirect_timer) {
			clearTimeout(this.rootStore.login_redirect_timer);
		}

		const token = localStorage.getItem('token');
		const without_login_page = ['/', '/login'];
		const dashboard_redirect_path = '/dashboard';
		const login_redirect_path = '/login';
		const setAppLoading = this.setAppLoading;

		this.rootStore.login_redirect_timer = setTimeout(function () {
			const currentPath = window.location.pathname;
			if (token && without_login_page.includes(currentPath)) {
				redirect = dashboard_redirect_path;
			} else if (!token && !without_login_page.includes(currentPath)) {
				redirect = login_redirect_path;
			}
			if (redirect) {
				if (redirect === 'reload') {
					window.location.reload();
				} else if (currentPath !== redirect) {
					window.location.href = redirect;
				}
			} else {
				setAppLoading(false);
			}
		}, timeout);
	}

	fetchAuthUser = async () => {
		try {
			const { data } = await axios.get(API_URL.ME);
			this.setUser(data.data);
			this.loading = false;
			return data;
		} catch (e) {
			localStorage.clear();
		}
	};

	setupHeaders = async (access_token) => {
		this.setAppLoading(true);
		this.rootStore
			.setAxiosHeaders(access_token)
			.then((token) => {
				this.setToken(token);
				this.fetchAuthUser();
			})
			.catch(() => {
				this.setToken();
				this.setUser();
			});
		this.customRedirect('', 10);
	};

	// Setter Functions
	setUser = (value) => {
		this.user = value;
	};
	setToken = (value) => {
		this.token = value;
	};
	setAppLoading = (value) => {
		this.app_loading = value;
	};

	// API Functions
	doLogin = async (payload) => {
		return await axios
			.post(API_URL.LOGIN, payload)
			.then(({ data }) => {
				if (data.httpStatusCode === 401) {
					this.setToken();
					this.setUser();
					return data;
				} else {
					this.setupHeaders(data.data);
					this.rootStore.setIsUnauthorise(false);
					return data;
				}
			})
			.catch((data) => {
				this.setToken();
				this.setUser();
				return Promise.reject(data);
			});
	};

	doLogout = async () => {
		this.rootStore.resetStore();
		this.setToken();
		this.setUser();
		// return await axios
		// 	.post(API_URL.LOGOUT)
		// 	.then(() => {
		// 		// this.setAppLoading(true);
		// 		this.rootStore.resetStore();
		// 		this.setToken();
		// 		this.setUser();
		// 	})
		// 	.catch((data) => {
		// 		// this.setAppLoading(false);
		// 		return Promise.reject(data);
		// 	});
	};

	getStateList = async () => {
		try {
			const { data } = await axios.get(
				`${API_URL.STATE}?regionType=${RegionType.State}`
			);
			this.stateList = data.data;
			return data.data;
		} catch {}
	};

	getAllByLookupKeyList = async (paramData) => {
		try {
			const { data } = await axios.get(
				`${API_URL.LOOKUPKEY}?${paramData}`
			);
			this.lookupKeyList = data.data;
			return data.data;
		} catch {}
	};

	getCityList = async (parentId) => {
		try {
			const { data } = await axios.get(
				`${API_URL.CITY}?parentId=${encodeURIComponent(parentId)}&regionType=${RegionType.City}`
			);
			this.cityList = data.data;
			return data.data;
		} catch {}
	};

	ImageUpload = async (formData) => {
		return await axios
			.post(API_URL.UPLOADIMAGE, formData)
			.then(({ data }) => {
				if (!data.data.errorMessage) {
					return data;
				} else {
					return data.data.errorMessage;
				}
			})
			.catch(({ data }) => {
				return Promise.reject(data);
			});
	};

	onRemoveImage = async (id, versionNo, isNotification) => {
		return await axios
			.delete(
				`${API_URL.DELETEIMAGE}?id=${encodeURIComponent(id)}&versionNo=${versionNo}`
			)
			.then(({ data }) => {
				if (data.data) {
					if (!isNotification) {
						Notification.error({ message: Message().global });
					}
					return data.data;
				} else {
					if (!isNotification) {
						Notification.success({ message: data.message });
					}
					return data.message;
				}
			})
			.catch(({ data }) => {
				return Promise.reject(data);
			});
	};

	onApproveImage = async (formData) => {
		return await axios
			.post(`${API_URL.APROVEIMAGE}`, formData)
			.then(({ data }) => {
				if (data.httpStatusCode == 200) {
					return data.message;
				} else {
					Notification.error({ message: data.message });
					return false;
				}
			})
			.catch(({ data }) => {
				return Promise.reject(data);
			});
	};
	getAmenityList = async (amenityType) => {
		try {
			const { data } = await axios.get(
				`${API_URL.AMENITY}?amenityType=${amenityType}`
			);
			this.amenityList = data.data;
			return data.data;
		} catch (error) {}
	};
}
